// src/pages/dashboard/admin/aulasAdmin.js
import React, { useEffect, useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";


import {
    Card,
    Button,
    Modal,
    Form,
    Alert,
    Row,
    Col,
    Spinner,
    OverlayTrigger,
    Tooltip,
    Dropdown
} from "react-bootstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

moment.locale("pt");

export default function AulasAdmin() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const calendarRef = useRef(null);

    const [aulas, setAulas] = useState([]);
    const [turmas, setTurmas] = useState([]);
    const [turmasAulas, setTurmasAulas] = useState([]);
    const [planoAulas, setPlanoAulas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteMode, setDeleteMode] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentAula, setCurrentAula] = useState(null);
    const [currentPlano, setCurrentPlano] = useState(null);

    const [showPast, setShowPast] = useState(false);

    const [recorrenteState, setRecorrenteState] = useState({
        enabled: false,
        semanaInicio: moment().startOf("month").format("YYYY-MM-DD"),
        semanaFim: moment().endOf("month").format("YYYY-MM-DD"),
        diasDaSemana: [],
        mesmaHora: true,
        horaInicioGlobal: "18:00",
        horaFimGlobal: "19:00",
        horasPorDia: {}
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [activeView, setActiveView] = useState(() =>
        typeof window !== "undefined" && window.innerWidth >= 768 ? "calendario" : "lista"
    );

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    // ---------- Helpers ----------
    const horaObjToHHmm = (obj) => {
        if (!obj) return "00:00";
        if (typeof obj === "string") {
            const [h, m] = obj.split(":");
            return `${h.padStart(2, "0")}:${(m || "00").padStart(2, "0")}`;
        }
        const h = String(obj.hours || 0).padStart(2, "0");
        const m = String(obj.minutes || 0).padStart(2, "0");
        return `${h}:${m}`;
    };

    const toggleRecDay = (d) => {
        const copy = [...recorrenteState.diasDaSemana];
        if (copy.includes(d)) setRecorrenteState({ ...recorrenteState, diasDaSemana: copy.filter((x) => x !== d) });
        else setRecorrenteState({ ...recorrenteState, diasDaSemana: [...copy, d] });
    };

    // ---------- Fetch ----------
    const fetchAll = async () => {
        setLoading(true); setError(""); setSuccess("");
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) { setError("Token não encontrado."); setLoading(false); return; }
        const headers = { Authorization: `Bearer ${token}` };
        try {
            const [aRes, tRes, taRes, pRes] = await Promise.all([
                fetch(`${API_BASE_URL}/aulas`, { headers }),
                fetch(`${API_BASE_URL}/turmas`, { headers }),
                fetch(`${API_BASE_URL}/turmasaulas`, { headers }),
                fetch(`${API_BASE_URL}/planoaulas`, { headers })
            ]);
            setAulas(aRes.ok ? await aRes.json() : []);
            setTurmas(tRes.ok ? await tRes.json() : []);
            setTurmasAulas(taRes.ok ? await taRes.json() : []);
            setPlanoAulas(pRes.ok ? await pRes.json() : []);
        } catch (err) {
            console.error(err);
            setError("Erro ao carregar dados.");
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchAll(); }, []);

    // ---------- FullCalendar ----------
    const events = aulas
        .slice()
        .sort((a, b) => {
            const da = new Date(a.data), db = new Date(b.data);
            if (da < db) return -1; if (da > db) return 1;
            const ta = horaObjToHHmm(a.hora_inicio), tb = horaObjToHHmm(b.hora_inicio);
            return ta > tb ? 1 : ta < tb ? -1 : 0;
        })
        .map((a) => {
            const mappings = turmasAulas.filter(
                (m) => Number(m.aulaId) === Number(a.id)
            );
            const turmaNames = mappings
                .map((m) =>
                    turmas.find((t) => Number(t.id) === Number(m.turmaId))?.turma
                )
                .filter(Boolean)
                .join(", ") || "Sem Turma";
            const plano = planoAulas.find((p) => p.aulaId === a.id) || null;
            const startTime = horaObjToHHmm(a.hora_inicio);
            const endTime = horaObjToHHmm(a.hora_fim);
            return {
                id: String(a.id),
                title: turmaNames,
                start: `${moment(a.data).format("YYYY-MM-DD")}T${startTime}:00`,
                end: `${moment(a.data).format("YYYY-MM-DD")}T${endTime}:00`,
                extendedProps: { sumario: a.sumario || "", plano, raw: a, turmaNames, displayTime: `${startTime} - ${endTime}` }
            };
        });

    const renderEventContent = (eventInfo) => {
        const ext = eventInfo.event.extendedProps;
        const tooltipContent = (
            <div style={{ whiteSpace: "pre-wrap", maxWidth: 300 }}>
                <strong>{eventInfo.event.title}</strong>
                <div style={{ fontSize: "0.9em", marginTop: 4 }}>{ext.displayTime}</div>
                {ext.plano?.conteudo && (
                    <div style={{ marginTop: 6, fontSize: "0.9em", color: "#555" }}>
                        <em>Plano:</em> {ext.plano.conteudo.substring(0, 120)}
                    </div>
                )}
            </div>
        );
        return (
            <OverlayTrigger placement="top" overlay={<Tooltip id={"tooltip-" + eventInfo.event.id}>{tooltipContent}</Tooltip>}>
                <div style={{ padding: "2px 4px" }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{eventInfo.event.title}</div>
                    <div style={{ fontSize: 11 }}>{ext.displayTime}</div>
                </div>
            </OverlayTrigger>
        );
    };

    // ---------- Modal helpers ----------
    const openModalForNew = (dateStr) => {
        setError(""); setSuccess(""); setIsEditing(false);
        setCurrentAula({ data: dateStr || moment().format("YYYY-MM-DD"), hora_inicio: "18:00", hora_fim: "19:00", turmasIds: [] });
        setCurrentPlano({ id: null, aulaId: null, sumario: "", conteudo: "", materiais: "", notas: "", posterior: "" });
        setRecorrenteState({
            enabled: false,
            semanaInicio: moment().startOf("month").format("YYYY-MM-DD"),
            semanaFim: moment().endOf("month").format("YYYY-MM-DD"),
            diasDaSemana: [],
            mesmaHora: true,
            horaInicioGlobal: "18:00",
            horaFimGlobal: "19:00",
            horasPorDia: {}
        });
        setShowModal(true);
    };

    const openModalForEdit = (aula) => {
        setError(""); setSuccess(""); setIsEditing(true);
        const mappings = turmasAulas.filter((m) => m.aulaId === aula.id);
        setCurrentAula({
            ...aula,
            data: moment(aula.data).format("YYYY-MM-DD"),
            hora_inicio: horaObjToHHmm(aula.hora_inicio),
            hora_fim: horaObjToHHmm(aula.hora_fim),
            turmasIds: mappings.map((m) => m.turmaId)
        });
        const plano = planoAulas.find((p) => p.aulaId === aula.id);
        setCurrentPlano(plano || { id: null, aulaId: aula.id, sumario: "", conteudo: "", materiais: "", notas: "", posterior: "" });
        setRecorrenteState({ ...recorrenteState, enabled: false });
        setShowModal(true);
    };

    const handleDateClick = (arg) => {
        if (deleteMode) {
            deleteAulasRange(deleteMode, moment(arg.date).format("YYYY-MM-DD"));
            setDeleteMode(null);
        } else {
            openModalForNew(moment(arg.date).format("YYYY-MM-DD"));
        }
    };

    const handleEventClick = (arg) => {
        const found = aulas.find((x) => String(x.id) === arg.event.id);
        if (found) openModalForEdit(found);
    };

    const deleteTurmaAula = async (turmaId, aulaId, headers) => {
        await fetch(
            `${API_BASE_URL}/turmasaulas?turmaId=${turmaId}&aulaId=${aulaId}`,
            { method: "DELETE", headers }
        );
    };


    // ---------- CRUD Aula ----------
    const saveAula = async () => {
        // Creates or updates the aula. Does not automatically create plano (unless you want that)
        setError(""); setSuccess(""); setLoading(true);
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };


        try {
            // If recorrente + not editing -> create many
            if (!isEditing && recorrenteState.enabled) {
                const start = moment(recorrenteState.semanaInicio);
                const end = moment(recorrenteState.semanaFim);
                let dates = [];
                for (let m = moment(start); m.isSameOrBefore(end); m.add(1, "days")) {
                    const dia = m.day();
                    if (!recorrenteState.diasDaSemana.includes(dia)) continue;
                    const horaInicio = recorrenteState.mesmaHora ? recorrenteState.horaInicioGlobal : recorrenteState.horasPorDia[dia]?.start || "18:00";
                    const horaFim = recorrenteState.mesmaHora ? recorrenteState.horaFimGlobal : recorrenteState.horasPorDia[dia]?.end || "19:00";
                    dates.push({
                        ...currentAula,
                        data: m.format("YYYY-MM-DD"),
                        hora_inicio: horaInicio,
                        hora_fim: horaFim,
                        turmasIds: currentAula.turmasIds || []
                    });
                }
                for (let aula of dates) {
                    // 1️⃣ Criar a aula
                    const res = await fetch(`${API_BASE_URL}/aulas`, {
                        method: "POST",
                        headers,
                        body: JSON.stringify({
                            data: aula.data,
                            hora_inicio: aula.hora_inicio + ":00",
                            hora_fim: aula.hora_fim + ":00",
                        })
                    });

                    if (!res.ok) {
                        throw new Error("Erro ao criar aula recorrente.");
                    }

                    const savedAula = await res.json();

                    for (const turmaId of aula.turmasIds || []) {
                        await fetch(`${API_BASE_URL}/turmasaulas`, {
                            method: "POST",
                            headers,
                            body: JSON.stringify({
                                aulaId: savedAula.id,
                                turmaId: turmaId
                            })
                        });
                    }
                }
            } else {
                // single create/update
                const method = isEditing ? "PUT" : "POST";
                const url = isEditing ? `${API_BASE_URL}/aulas/${currentAula.id}` : `${API_BASE_URL}/aulas`;
                // Ensure we send expected fields
                const payload = {
                    ...currentAula,
                    data: currentAula.data,
                    hora_inicio: horaObjToHHmm(currentAula.hora_inicio) + ":00",
                    hora_fim: horaObjToHHmm(currentAula.hora_fim) + ":00",
                };

                const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
                if (!res.ok) throw new Error(isEditing ? "Falha ao atualizar aula." : "Falha ao criar aula.");

                /* const savedAula = await res.json();
                if (!savedAula?.id) {
                    throw new Error("A API não devolveu o ID da aula criada.");
                }

                const aulaId = savedAula.id; */

                let aulaId;

                if (!isEditing) {
                    const savedAula = await res.json();

                    if (!savedAula?.id) {
                        throw new Error("A API não devolveu o ID da aula criada.");
                    }

                    aulaId = savedAula.id;
                }
                else {
                    aulaId = currentAula.id;
                }

                // Update turmas mappings => delete existing then create new ones
                const existingMappings = turmasAulas.filter((m) => m.aulaId === aulaId);
                for (let m of existingMappings) {
                    await deleteTurmaAula(m.turmaId, m.aulaId, headers);
                }

                for (let turmaId of currentAula.turmasIds || []) {
                    await fetch(`${API_BASE_URL}/turmasaulas`, { method: "POST", headers, body: JSON.stringify({ aulaId, turmaId }) });
                }

                // NOTE: we do not auto-create plano here; user can press "Guardar Plano"
                // But if there is a currentPlano with content and it already existed, update it
                if (currentPlano?.id) {
                    // update plano if changed (optimistic)
                    const hasPlanoContent = Object.values(currentPlano).some(val => typeof val === 'string' && val.length > 0);
                    if (hasPlanoContent) {
                        await fetch(`${API_BASE_URL}/planoaulas/${currentPlano.id}`, { method: "PUT", headers, body: JSON.stringify({ ...currentPlano, aulaId }) });
                    }
                }
                // ensure currentAula has id for further actions
                setCurrentAula({ ...currentAula, id: aulaId });
            }

            setSuccess("Aula guardada com sucesso.");
            await fetchAll();
            setShowModal(false);
        } catch (err) {
            console.error(err);
            setError(err.message || "Erro ao guardar aula.");
        } finally { setLoading(false); }
    };

    const deleteAula = async (id) => {
        if (!window.confirm("Tem a certeza que quer apagar esta aula e o seu plano associado?")) return;
        setLoading(true); setError(""); setSuccess("");
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        try {

            const mappings = turmasAulas.filter(m => m.aulaId === id);
            for (let m of mappings) {
                await deleteTurmaAula(m.turmaId, m.aulaId, headers);
            }

            const plano = planoAulas.find(p => p.aulaId === id);
            if (plano && plano.id) {
                await fetch(`${API_BASE_URL}/planoaulas/${plano.id}`, { method: "DELETE", headers });
            }

            await fetch(`${API_BASE_URL}/aulas/${id}`, { method: "DELETE", headers });
            setSuccess("Aula apagada com sucesso.");
            await fetchAll();
            setShowModal(false);
        } catch (err) {
            console.error(err);
            setError("Erro ao apagar aula.");
        } finally { setLoading(false); }
    };

    const deleteAulasRange = async (tipo, dataRef) => {
        if (!window.confirm(`Tem a certeza que quer apagar TODAS as aulas deste ${tipo}? Esta ação é irreversível.`)) return;
        setLoading(true); setError(""); setSuccess("");
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        try {
            let aulasToDelete = [];
            if (tipo === "dia") {
                aulasToDelete = aulas.filter((a) => moment(a.data).isSame(dataRef, 'day'));
            } else if (tipo === "semana") {
                const startOfWeek = moment(dataRef).startOf("week");
                const endOfWeek = moment(dataRef).endOf("week");
                aulasToDelete = aulas.filter((a) => moment(a.data).isBetween(startOfWeek, endOfWeek, 'day', "[]"));
            } else if (tipo === "mes") {
                const startOfMonth = moment(dataRef).startOf("month");
                const endOfMonth = moment(dataRef).endOf("month");
                aulasToDelete = aulas.filter((a) => moment(a.data).isBetween(startOfMonth, endOfMonth, 'day', "[]"));
            }

            if (aulasToDelete.length === 0) {
                setSuccess("Nenhuma aula encontrada para apagar.");
                setLoading(false);
                return;
            }

            for (let aula of aulasToDelete) {
                // delete mappings
                const mappings = turmasAulas.filter(m => m.aulaId === aula.id);
                for (let m of mappings) {
                    await deleteTurmaAula(m.turmaId, m.aulaId, headers);
                }

                // delete plano if exists
                const plano = planoAulas.find(p => p.aulaId === aula.id);
                if (plano && plano.id) {
                    await fetch(`${API_BASE_URL}/planoaulas/${plano.id}`, { method: "DELETE", headers });
                }
                // delete aula
                await fetch(`${API_BASE_URL}/aulas/${aula.id}`, { method: "DELETE", headers });
            }

            setSuccess(`${aulasToDelete.length} aulas do ${tipo} foram apagadas.`);
            await fetchAll();
        } catch (err) {
            console.error(err);
            setError("Erro ao apagar aulas em lote.");
        } finally { setLoading(false); }
    };

    // ---------- Plano de Aula CRUD (frontend) ----------
    const savePlanoAula = async () => {
        setError("");
        setSuccess("");
        setLoading(true);

        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) { setError("Token não encontrado."); setLoading(false); return; }

        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

        try {
            let aulaId = currentAula?.id;

            // 1️⃣ Se a aula ainda não existe, cria-a primeiro
            if (!aulaId) {
                const payloadAula = {
                    data: currentAula.data,
                    hora_inicio: horaObjToHHmm(currentAula.hora_inicio) + ":00",
                    hora_fim: horaObjToHHmm(currentAula.hora_fim) + ":00",
                    turmasIds: currentAula.turmasIds || []
                };

                const res = await fetch(`${API_BASE_URL}/aulas`, {
                    method: "POST",
                    headers,
                    body: JSON.stringify(payloadAula)
                });

                if (!res.ok) throw new Error("Falha ao criar aula antes de guardar o plano.");

                const createdAula = await res.json();
                aulaId = createdAula.id;
                if (!aulaId) throw new Error("API não devolveu o ID da aula.");

                setCurrentAula({ ...currentAula, id: aulaId });
            }

            // 2️⃣ Monta payload do plano
            const planoPayload = {
                id: currentPlano?.id || 0,
                aulaId,
                sumario: currentPlano?.sumario || "",
                conteudo: currentPlano?.conteudo || "",
                materiais: currentPlano?.materiais || "",
                notas: currentPlano?.notas || "",
                posterior: currentPlano?.posterior || ""
            };

            let url = `${API_BASE_URL}/planoaulas`;
            let method = "POST";

            if (currentPlano?.id && currentPlano.id > 0) {
                url += `/${currentPlano.id}`;
                method = "PUT";
            }

            const resPlano = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(planoPayload)
            });

            if (!resPlano.ok) {
                const txt = await resPlano.text(); // lê como texto
                console.error("Erro API plano:", txt);
                throw new Error(method === "POST" ? "Falha ao criar o plano." : "Falha ao atualizar o plano.");
            }

            // Só tenta ler JSON se houver conteúdo
            let updatedPlano = planoPayload;
            const text = await resPlano.text();
            if (text) {
                updatedPlano = JSON.parse(text);
            }

            setCurrentPlano({ ...planoPayload, id: updatedPlano.id || planoPayload.id });
            setSuccess(method === "POST" ? "Plano de aula criado." : "Plano de aula atualizado.");
            await fetchAll();

        } catch (err) {
            console.error(err);
            setError(err.message || "Erro ao guardar plano.");
        } finally {
            setLoading(false);
        }
    };


    const deletePlanoAula = async () => {
        if (!currentPlano?.id) { setError("Não existe plano para apagar."); return; }
        if (!window.confirm("Tem a certeza que quer apagar o plano desta aula?")) return;
        setLoading(true); setError(""); setSuccess("");
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        try {
            await fetch(`${API_BASE_URL}/planoaulas/${currentPlano.id}`, { method: "DELETE", headers });
            setCurrentPlano({ id: null, aulaId: currentAula?.id || null, sumario: "", conteudo: "", materiais: "", notas: "", posterior: "" });
            setSuccess("Plano apagado.");
            await fetchAll();
        } catch (err) {
            console.error(err);
            setError("Erro ao apagar plano.");
        } finally { setLoading(false); }
    };

    // ---------- UI helper ----------
    const renderTurmasBadges = (turmasIds = []) =>
        (turmasIds || []).map((id) => {
            const t = turmas.find((x) => x.id === id);
            if (!t) return null;
            return <span key={id} className="badge bg-secondary me-1">{t.turma}</span>;
        });

    // ---------- Render ----------
    return (
        <>
            <div className="d-flex justify-content-between flex-wrap gap-3 align-items-start py-4">
                <div>
                    <h1 className="h2">Calendário de Aulas</h1>
                    <p className="mb-0">Clique num dia para criar ou numa aula para editar.</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <Button variant="primary" onClick={() => openModalForNew()}>
                        <FontAwesomeIcon icon={faPlus} className="me-2" /> Adicionar Aula
                    </Button>
                    <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
                        <FontAwesomeIcon icon={faTrashAlt} className="me-2" /> Apagar Aulas
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}
            {loading && <div className="mb-2"><Spinner animation="border" size="sm" /> A carregar...</div>}

            {/* Tab switcher */}
            <div style={{ display: "flex", background: "#f0f0f5", borderRadius: 14, padding: 4, marginBottom: 20 }}>
                {[{ key: "lista", label: "Lista" }, { key: "calendario", label: "Calendário" }].map(tab => (
                    <button key={tab.key} onClick={() => setActiveView(tab.key)} style={{
                        flex: 1, border: "none", borderRadius: 10, padding: "11px 0",
                        fontWeight: 600, fontSize: 14, cursor: "pointer", transition: "all 0.2s ease",
                        backgroundColor: activeView === tab.key ? "white" : "transparent",
                        color: activeView === tab.key ? "#1e1d45" : "#9ca3af",
                        boxShadow: activeView === tab.key ? "0 2px 10px rgba(0,0,0,0.12)" : "none",
                        fontFamily: "inherit",
                    }}>{tab.label}</button>
                ))}
            </div>

            {/* ===== VISTA LISTA ===== */}
            {activeView === "lista" && (() => {
                const now = moment();
                const aulasEnriquecidas = [...events]
                    .sort((a, b) => new Date(a.start) - new Date(b.start));
                const futuras = aulasEnriquecidas.filter(e => moment(e.start).isSameOrAfter(now));
                const passadas = aulasEnriquecidas.filter(e => moment(e.start).isBefore(now));

                const AulaCard = ({ ev, isPast }) => {
                    const dt = moment(ev.start);
                    const isHoje = dt.isSame(now, "day");
                    const isAmanha = dt.isSame(moment().add(1, "day"), "day");
                    const diasRestantes = dt.diff(now, "days");
                    let badge = null;
                    if (isHoje) badge = { label: "Hoje", bg: "#198754" };
                    else if (isAmanha) badge = { label: "Amanhã", bg: "#0d6efd" };
                    else if (!isPast && diasRestantes <= 6) badge = { label: `em ${diasRestantes + 1}d`, bg: "#fd7e14" };
                    const endDt = moment(ev.end);
                    const startStr = dt.format("HH:mm");
                    const endStr = endDt.format("HH:mm");
                    return (
                        <div
                            onClick={() => { const found = aulas.find(a => String(a.id) === ev.id); if (found) openModalForEdit(found); }}
                            style={{
                                display: "flex", alignItems: "stretch", backgroundColor: "white", borderRadius: 14,
                                overflow: "hidden", marginBottom: 10, opacity: isPast ? 0.6 : 1, cursor: "pointer",
                                border: isHoje ? "2px solid #198754" : isAmanha ? "2px solid #0d6efd" : "1px solid #e9ecef",
                                boxShadow: isPast ? "none" : "0 2px 8px rgba(0,0,0,0.07)",
                            }}
                        >
                            <div style={{
                                width: 68, minWidth: 68, background: isPast ? "#adb5bd" : isHoje ? "#198754" : isAmanha ? "#0d6efd" : "#1e1d45",
                                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                padding: "10px 4px", color: "white", textAlign: "center",
                            }}>
                                <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.85 }}>{dt.format("ddd")}</span>
                                <span style={{ fontSize: 28, fontWeight: 900, lineHeight: 1, margin: "2px 0" }}>{dt.format("DD")}</span>
                                <span style={{ fontSize: 10, opacity: 0.85 }}>{dt.format("MMM")}</span>
                            </div>
                            <div style={{ flex: 1, padding: "12px 14px", display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, marginBottom: 5 }}>
                                    <span style={{ fontWeight: 700, fontSize: 15, color: "#1e1d45", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {ev.title}
                                    </span>
                                    {badge && (
                                        <span style={{ fontSize: 10, fontWeight: 700, color: "white", backgroundColor: badge.bg, borderRadius: 20, padding: "3px 8px", flexShrink: 0 }}>
                                            {badge.label}
                                        </span>
                                    )}
                                </div>
                                <div style={{ color: "#6c757d", fontSize: 13 }}>{startStr} – {endStr}</div>
                            </div>
                        </div>
                    );
                };

                const agruparPorMes = (lista) => {
                    const grupos = {};
                    lista.forEach(e => {
                        const chave = moment(e.start).format("MMMM YYYY");
                        if (!grupos[chave]) grupos[chave] = [];
                        grupos[chave].push(e);
                    });
                    return grupos;
                };

                const gruposFuturos = agruparPorMes(futuras);

                return (
                    <>
                        {/* Stats */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 24 }}>
                            {[
                                { label: "Próximas", value: futuras.length, color: "#1e1d45" },
                                { label: "Este mês", value: events.filter(e => moment(e.start).isSame(now, "month")).length, color: "#0d6efd" },
                                { label: "Realizadas", value: passadas.length, color: "#6c757d" },
                            ].map(s => (
                                <div key={s.label} style={{ background: "white", borderRadius: 14, padding: "14px 8px", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", borderTop: `4px solid ${s.color}` }}>
                                    <div style={{ fontSize: 24, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
                                    <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>

                        {Object.keys(gruposFuturos).length === 0 ? (
                            <div style={{ textAlign: "center", padding: "48px 20px", background: "white", borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                                <p className="text-muted mb-0 fw-bold">Sem aulas agendadas.</p>
                            </div>
                        ) : (
                            Object.entries(gruposFuturos).map(([mes, lista]) => (
                                <div key={mes} style={{ marginBottom: 28 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: "2px solid #e8eaf6" }}>
                                        <span style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1, color: "#495057" }}>{mes}</span>
                                        <span style={{ background: "#1e1d45", color: "white", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>{lista.length}</span>
                                    </div>
                                    {lista.map(e => <AulaCard key={e.id} ev={e} isPast={false} />)}
                                </div>
                            ))
                        )}

                        {passadas.length > 0 && (
                            <>
                                <button onClick={() => setShowPast(v => !v)} style={{
                                    width: "100%", border: "2px dashed #dee2e6", borderRadius: 12, background: "transparent",
                                    color: "#6c757d", padding: "13px", fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 16,
                                }}>
                                    {showPast ? "Ocultar aulas passadas" : `Ver ${passadas.length} aulas passadas`}
                                </button>
                                {showPast && Object.entries(agruparPorMes([...passadas].reverse())).map(([mes, lista]) => (
                                    <div key={mes} style={{ marginBottom: 28 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: "2px solid #f0f0f5" }}>
                                            <span style={{ fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1, color: "#adb5bd" }}>{mes}</span>
                                            <span style={{ background: "#6c757d", color: "white", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>{lista.length}</span>
                                        </div>
                                        {lista.map(e => <AulaCard key={e.id} ev={e} isPast={true} />)}
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                );
            })()}

            {/* ===== VISTA CALENDÁRIO ===== */}
            {activeView === "calendario" && (
                <Card border="light" className="shadow-sm mb-4">
                    <Card.Body className="p-2 p-md-3">
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            headerToolbar={{ left: "prev,next", center: "title", right: "today" }}
                            footerToolbar={{ center: "dayGridMonth,timeGridWeek,timeGridDay" }}
                            events={events}
                            dateClick={handleDateClick}
                            eventClick={handleEventClick}
                            editable={true}
                            selectable={true}
                            ref={calendarRef}
                            height="auto"
                            locale="pt"
                            eventContent={renderEventContent}
                        />
                    </Card.Body>
                </Card>
            )}

            {/* Modal Aula + Plano */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? "Editar Aula" : "Adicionar Aula"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={(e) => { e.preventDefault(); saveAula(); }}>
                        {/* Aula Info (no sumário) */}
                        <Row className="mb-3">
                            <Col md={4}><Form.Group><Form.Label>Data</Form.Label><Form.Control type="date" value={currentAula?.data || ""} onChange={(e) => setCurrentAula({ ...currentAula, data: e.target.value })} required disabled={recorrenteState.enabled} /></Form.Group></Col>
                            <Col md={4}><Form.Group><Form.Label>Hora Início</Form.Label><Form.Control type="time" value={currentAula?.hora_inicio || ""} onChange={(e) => setCurrentAula({ ...currentAula, hora_inicio: e.target.value })} required disabled={recorrenteState.enabled} /></Form.Group></Col>
                            <Col md={4}><Form.Group><Form.Label>Hora Fim</Form.Label><Form.Control type="time" value={currentAula?.hora_fim || ""} onChange={(e) => setCurrentAula({ ...currentAula, hora_fim: e.target.value })} required disabled={recorrenteState.enabled} /></Form.Group></Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label>Turmas</Form.Label>
                            <div>
                                {turmas.map((t) => (
                                    <Form.Check inline key={t.id} type="checkbox" label={t.turma} checked={currentAula?.turmasIds?.includes(t.id)} onChange={() => {
                                        const selected = currentAula.turmasIds || [];
                                        if (selected.includes(t.id)) setCurrentAula({ ...currentAula, turmasIds: selected.filter((x) => x !== t.id) });
                                        else setCurrentAula({ ...currentAula, turmasIds: [...selected, t.id] });
                                    }} />
                                ))}
                            </div>
                            <div className="mt-2">{renderTurmasBadges(currentAula?.turmasIds || [])}</div>
                        </Form.Group>

                        {/* Aula Recorrente */}
                        {!isEditing && (
                            <>
                                <hr />
                                <Form.Group className="mb-3"><Form.Check type="switch" label="Criar Aulas Recorrentes" checked={recorrenteState.enabled} onChange={(e) => setRecorrenteState({ ...recorrenteState, enabled: e.target.checked })} /></Form.Group>
                                {recorrenteState.enabled && (
                                    <>
                                        <Row className="mb-3">
                                            <Col md={6}><Form.Group><Form.Label>Data Início</Form.Label><Form.Control type="date" value={recorrenteState.semanaInicio} onChange={(e) => setRecorrenteState({ ...recorrenteState, semanaInicio: e.target.value })} /></Form.Group></Col>
                                            <Col md={6}><Form.Group><Form.Label>Data Fim</Form.Label><Form.Control type="date" value={recorrenteState.semanaFim} onChange={(e) => setRecorrenteState({ ...recorrenteState, semanaFim: e.target.value })} /></Form.Group></Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Dias da Semana</Form.Label>
                                            <div className="d-flex gap-2">
                                                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((dia, idx) => (
                                                    <Button key={idx} variant={recorrenteState.diasDaSemana.includes(idx) ? "primary" : "outline-primary"} size="sm" onClick={() => toggleRecDay(idx)}>{dia}</Button>
                                                ))}
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3"><Form.Check type="switch" label="Mesma hora todos os dias" checked={recorrenteState.mesmaHora} onChange={(e) => setRecorrenteState({ ...recorrenteState, mesmaHora: e.target.checked })} /></Form.Group>
                                        {recorrenteState.mesmaHora ? (
                                            <Row className="mb-3">
                                                <Col md={6}><Form.Group><Form.Label>Hora Início Global</Form.Label><Form.Control type="time" value={recorrenteState.horaInicioGlobal} onChange={(e) => setRecorrenteState({ ...recorrenteState, horaInicioGlobal: e.target.value })} /></Form.Group></Col>
                                                <Col md={6}><Form.Group><Form.Label>Hora Fim Global</Form.Label><Form.Control type="time" value={recorrenteState.horaFimGlobal} onChange={(e) => setRecorrenteState({ ...recorrenteState, horaFimGlobal: e.target.value })} /></Form.Group></Col>
                                            </Row>
                                        ) : (
                                            <div>
                                                {recorrenteState.diasDaSemana.sort().map((d) => (
                                                    <Row className="mb-2 align-items-center" key={d}>
                                                        <Col xs="auto" className="fw-bold">{["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][d]}</Col>
                                                        <Col><Form.Control type="time" placeholder="Início" value={recorrenteState.horasPorDia[d]?.start || ""} onChange={(e) => setRecorrenteState({ ...recorrenteState, horasPorDia: { ...recorrenteState.horasPorDia, [d]: { ...recorrenteState.horasPorDia[d], start: e.target.value } } })} /></Col>
                                                        <Col><Form.Control type="time" placeholder="Fim" value={recorrenteState.horasPorDia[d]?.end || ""} onChange={(e) => setRecorrenteState({ ...recorrenteState, horasPorDia: { ...recorrenteState.horasPorDia, [d]: { ...recorrenteState.horasPorDia[d], end: e.target.value } } })} /></Col>
                                                    </Row>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* Plano de Aula */}
                        <hr />
                        <h5 className="mb-3">Plano de Aula</h5>
                        <Form.Group className="mb-3"><Form.Label>Sumário</Form.Label><Form.Control as="textarea" rows={2} value={currentPlano?.sumario || ""} onChange={(e) => setCurrentPlano({ ...currentPlano, sumario: e.target.value })} /></Form.Group>
                        <Form.Group className="mb-3"><Form.Label>Conteúdo Programado</Form.Label><Form.Control as="textarea" rows={4} value={currentPlano?.conteudo || ""} onChange={(e) => setCurrentPlano({ ...currentPlano, conteudo: e.target.value })} /></Form.Group>
                        <Form.Group className="mb-3"><Form.Label>Materiais</Form.Label><Form.Control as="textarea" rows={2} value={currentPlano?.materiais || ""} onChange={(e) => setCurrentPlano({ ...currentPlano, materiais: e.target.value })} /></Form.Group>
                        <Row>
                            <Col md={6}><Form.Group className="mb-3"><Form.Label>Notas</Form.Label><Form.Control as="textarea" rows={3} value={currentPlano?.notas || ""} onChange={(e) => setCurrentPlano({ ...currentPlano, notas: e.target.value })} /></Form.Group></Col>
                            <Col md={6}><Form.Group className="mb-3"><Form.Label>Observações da Aula</Form.Label><Form.Control as="textarea" rows={3} value={currentPlano?.posterior || ""} onChange={(e) => setCurrentPlano({ ...currentPlano, posterior: e.target.value })} /></Form.Group></Col>
                        </Row>

                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-2 mt-3">
                            <div className="d-flex gap-2 flex-wrap">
                                {isEditing && <Button variant="outline-danger" onClick={() => deleteAula(currentAula.id)}><FontAwesomeIcon icon={faTrashAlt} /> Apagar Aula</Button>}
                                {currentPlano?.id && <Button variant="outline-danger" onClick={deletePlanoAula}>Apagar Plano</Button>}
                            </div>
                            <div className="d-flex gap-2 flex-wrap justify-content-end">
                                <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                                <Button variant="info" onClick={savePlanoAula} disabled={loading}>
                                    {loading ? <Spinner as="span" animation="border" size="sm" /> : (currentPlano?.id ? "Salvar Plano" : "Criar Plano")}
                                </Button>
                                <Button variant="primary" type="submit" disabled={loading}>
                                    {loading ? <Spinner as="span" animation="border" size="sm" /> : (isEditing ? "Salvar Alterações" : "Criar Aula")}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Delete modal (choose day/week/month then click calendar day) */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Apagar Aulas em Lote</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Escolha o tipo de eliminação (depois clique no dia correspondente no calendário):</p>
                    <div className="d-flex flex-column gap-2">
                        <Button variant="outline-danger" onClick={() => { setDeleteMode("dia"); setShowDeleteModal(false); }}>
                            Apagar por Dia
                        </Button>
                        <Button variant="outline-danger" onClick={() => { setDeleteMode("semana"); setShowDeleteModal(false); }}>
                            Apagar por Semana
                        </Button>
                        <Button variant="outline-danger" onClick={() => { setDeleteMode("mes"); setShowDeleteModal(false); }}>
                            Apagar por Mês
                        </Button>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancelar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
